import { createTheme } from '@mui/material/styles'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { FONT_FAMILY_SANS, typeScale } from '@/shared/styles/typography'
import { RADIUS } from '@/shared/styles/radii'

type TypeVariant = {
  fontSize: number
  lineHeight: number
  fontWeight: number
  letterSpacing?: string
}

// 文字サイズモードの倍率を theme に載せ、 variant 非経由のインライン fontSize
// (scaledFontSize) からも参照できるようにする (ADR-0019).
declare module '@mui/material/styles' {
  interface Theme {
    fontScale: number
  }
  interface ThemeOptions {
    fontScale?: number
  }
}

// fontScale を typeScale variant に乗算する (px は整数丸め). lineHeight は
// 相対値ゆえスケール対象外. fontScale > 1 で本文が拡大する.
const scaleVariant = (
  variant: TypeVariant,
  fontScale: number
): TypeVariant => ({
  ...variant,
  fontSize: Math.round(variant.fontSize * fontScale),
})

/**
 * MUI Typography variant ↔ 28 spec typeScale 対応:
 *  - h1-h4    : 28 spec h1-h4 と同名同値
 *  - body1    : 28 spec body
 *  - body2    : 28 spec bodySm (14px)
 *  - button   : 28 spec label   (13px、Notion で最頻出)
 *  - caption  : 28 spec tag     (12px)
 *  - subtitle1: 28 spec body サイズに medium weight (28 spec 階層に存在しないが既存 portal callsite との互換性維持)
 *  - subtitle2: 28 spec bodySm サイズに medium weight (同上)
 */

/**
 * MUI theme — 28 spec トークンを palette/typography/shape/components に移植
 *
 * 一次情報: docs/design-system/03_ui_implementation_spec.md
 *
 * 使い方:
 *  - 色は `colorTokens.{bg,fg,accent,state}.*` を import して直接参照する
 *    (palette 経由でも OK だが、token 名が文書化された source-of-truth)
 *  - sx の `bgcolor: 'background.paper'` 等の MUI 経由参照は palette 値を返す
 */
export const createAppTheme = (fontScale: number = 1) =>
  createTheme({
    fontScale,
    palette: {
      background: {
        default: colorTokens.bg.default,
        paper: colorTokens.bg.subtle,
      },
      text: {
        primary: colorTokens.fg.default,
        secondary: colorTokens.fg.muted,
        disabled: colorTokens.fg.faint,
      },
      primary: {
        main: colorTokens.accent.primary,
        dark: colorTokens.accent.primaryHover,
        contrastText: colorTokens.accent.primaryFg,
      },
      secondary: {
        main: colorTokens.fg.muted,
        dark: colorTokens.fg.default,
        light: colorTokens.bg.emphasis,
      },
      action: {
        selected: colorTokens.bg.muted,
        selectedOpacity: 0.08,
        hover: 'rgba(55, 53, 47, 0.04)',
        hoverOpacity: 0.04,
        focus: 'rgba(55, 53, 47, 0.08)',
        focusOpacity: 0.08,
        disabled: colorTokens.fg.disabled,
        disabledBackground: colorTokens.bg.subtle,
      },
      info: {
        main: colorTokens.state.infoFg,
        light: colorTokens.state.infoBg,
      },
      warning: {
        main: colorTokens.state.warnFg,
        light: colorTokens.state.warnBg,
      },
      error: {
        main: colorTokens.state.dangerFg,
        light: colorTokens.state.dangerBg,
      },
      success: {
        main: colorTokens.state.successFg,
        light: colorTokens.state.successBg,
      },
      divider: colorTokens.border.default,
    },
    typography: {
      fontFamily: FONT_FAMILY_SANS,
      h1: scaleVariant(typeScale.h1, fontScale),
      h2: scaleVariant(typeScale.h2, fontScale),
      h3: scaleVariant(typeScale.h3, fontScale),
      h4: scaleVariant(typeScale.h4, fontScale),
      body1: scaleVariant(typeScale.body, fontScale),
      body2: scaleVariant(typeScale.bodySm, fontScale),
      button: {
        ...scaleVariant(typeScale.label, fontScale),
        textTransform: 'none' as const,
      },
      caption: scaleVariant(typeScale.tag, fontScale),
      subtitle1: {
        ...scaleVariant(typeScale.body, fontScale),
        fontWeight: 500,
      },
      subtitle2: {
        ...scaleVariant(typeScale.bodySm, fontScale),
        fontWeight: 500,
      },
    },
    shape: { borderRadius: RADIUS.md },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiAppBar: { defaultProps: { elevation: 0 } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: RADIUS.full, textTransform: 'none' },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', minHeight: 28 } },
      },
      // Notion 流 warm gray pattern. MUI default の primary blue 系 border/ripple を
      // 全 app で抑制. design/16 §5 で portal scope override (= PORTAL_NOTION_OVERRIDE_SX)
      // を theme まで hoist した.
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: 'rgba(55, 53, 47, 0.16)',
          },
          root: {
            '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'rgba(55, 53, 47, 0.3)',
              },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(55, 53, 47, 0.06)',
            },
          },
        },
      },
    },
  })

// 後方互換: 既存 import (= ThemeProvider 直渡し等) はそのまま等倍 theme を参照する.
export const theme = createAppTheme(1)

export { colorTokens }
